import { http, HttpResponse, passthrough } from 'msw';
import { v4 as uuid } from 'uuid';

import { base64Url } from './jwtMockHelpers';

// /get_access_token's real request body is `{ code }` only -- it never carries `scope` -- so a real SBL
// code exchange can't be told apart from a mocked one by scope there the way it can on the other three
// endpoints. Discriminate on this exact prefix instead: only a code starting with it is ours to answer, and
// the country suffix carries the countryCode /init received through to get_access_token, which otherwise has
// no country field of its own.
const DEMO_FAKE_AUTH_CODE_PREFIX = 'msw-demo-fake-auth-code';
// refresh_access_token/revoke_token have no country field either (real body is just `{ refreshToken, scope
// }`), so the country rides along in the refresh token's own suffix instead, minted below.
const DEMO_REFRESH_TOKEN_PREFIX = 'msw-demo-refresh-token';
const SBL_SCOPE = 'cl-sbl-NO';

// oba-auth-service formats the real `sub` claim as `<countryCode><governmentId>` (see
// extractGovernmentIdFromSubClaim's doc comment in helpers/jwt.ts). DK's own eSkat/KreditStatus consent
// pages (main-applicant and co-applicant) extract a government id from this claim after their own MitID
// login -- give the mock token one so that still works, rather than silently losing the government id.
const buildDemoAccessToken = (countryCode: string) => {
    const payload = {
        sub: `${countryCode}0101011234`,
        exp: Math.floor(Date.now() / 1000) + 60 * 60,
    };
    return `${base64Url({ alg: 'none', typ: 'JWT' })}.${base64Url(payload)}.demo`;
};

// The trailing uuid makes every minted refresh_token unique per response, not just per country: the
// identity guards in authenticationStore.ts (refreshAccessToken.fulfilled/.rejected) tell a stale response
// apart from a current one by comparing refresh_token values, which only works if a new demo session
// (even for the same country) can't end up with the exact same refresh_token an old, invalidated session
// already had in flight.
const buildAuthenticationData = (countryCode: string) => ({
    access_token: buildDemoAccessToken(countryCode),
    token_type: 'Bearer',
    expires_in: 3600,
    refresh_token_expires_in: 86400,
    refresh_token: `${DEMO_REFRESH_TOKEN_PREFIX}-${countryCode}-${uuid()}`,
    scope: 'demo',
});

// Best-effort parse of the country this mock itself encoded into a code/refresh token; falls back to DK
// (the only flow that currently reads the sub claim) if the format doesn't match, e.g. under jest mocks that
// hand back a bare literal instead of going through the real handlers. Only the segment right after the
// prefix is the country -- refresh tokens carry a uuid after it too (see buildAuthenticationData above).
const parseCountryCode = (prefix: string, value: string | undefined) => {
    if (!value?.startsWith(`${prefix}-`)) {
        return 'DK';
    }
    const [countryCode] = value.slice(prefix.length + 1).split('-');
    return countryCode || 'DK';
};

// Narrower than msw's real resolver context type -- these resolvers only ever call request.json(), and
// typing against that (rather than the full DOM Request) lets tests call them with a lightweight fake
// request instead of a real one, while still being a valid msw resolver (DOM's Request structurally
// satisfies this narrower shape).
type JsonRequest = { json: () => Promise<unknown> };

// Named (rather than only reachable positionally through the `oauthHandlers` array below) so tests can
// import the resolver they mean to exercise directly -- an array-destructure keyed on declaration order
// would silently start testing the wrong endpoint if these were ever reordered.
export const initResolver = async ({ request }: { request: JsonRequest }) => {
    const body = (await request.json()) as { countryCode: string; redirectUrl: string; state: string; scope?: string };
    if (body.scope === SBL_SCOPE) {
        // NO's real SBL income-verification exchange (VerifyIncomePageNO/CoApplicantLogInPage) -- let
        // it hit the real, cross-origin BankID/SBL backend untouched.
        return passthrough();
    }
    // Real /init returns response.text() -- a bare URL string, not JSON. Echo the caller's own
    // redirectUrl and state back as query params: initAuthentication's getAccessToken thunk validates
    // the state it gets back on return against what it itself generated and stored in Redux, so this
    // must be the real body.state, not a placeholder -- a mismatch silently fails the exchange.
    const url = new URL(body.redirectUrl);
    url.searchParams.set('code', `${DEMO_FAKE_AUTH_CODE_PREFIX}-${body.countryCode}`);
    url.searchParams.set('state', body.state);
    return new HttpResponse(url.toString(), { status: 200, headers: { 'Content-Type': 'text/plain' } });
};

export const getAccessTokenResolver = async ({ request }: { request: JsonRequest }) => {
    const body = (await request.json()) as { code?: string };
    if (!body.code?.startsWith(`${DEMO_FAKE_AUTH_CODE_PREFIX}-`)) {
        // Not a code this mock minted -- e.g. a real code from SBL's real /init exchange above.
        return passthrough();
    }
    return HttpResponse.json(buildAuthenticationData(parseCountryCode(DEMO_FAKE_AUTH_CODE_PREFIX, body.code)));
};

export const refreshAccessTokenResolver = async ({ request }: { request: JsonRequest }) => {
    const body = (await request.json()) as { refreshToken?: string; scope?: string };
    if (body.scope === SBL_SCOPE) {
        return passthrough();
    }
    return HttpResponse.json(buildAuthenticationData(parseCountryCode(DEMO_REFRESH_TOKEN_PREFIX, body.refreshToken)));
};

export const revokeTokenResolver = async ({ request }: { request: JsonRequest }) => {
    const body = (await request.json()) as { scope?: string };
    if (body.scope === SBL_SCOPE) {
        return passthrough();
    }
    return new HttpResponse(null, { status: 200 });
};

export const oauthHandlers = [
    http.post('*/api/cl_loanapp_proxy_service/auth/init', initResolver),
    http.post('*/api/cl_loanapp_proxy_service/auth/get_access_token', getAccessTokenResolver),
    http.post('*/api/cl_loanapp_proxy_service/auth/refresh_access_token', refreshAccessTokenResolver),
    http.post('*/api/cl_loanapp_proxy_service/auth/revoke_token', revokeTokenResolver),
];
